import { CheckIcon } from 'lucide-react';
import * as React from 'react';

import { cn, getTimezoneAbbreviation } from '@/lib/utils';

export interface SessionCardProps {
  scheduleDetails: {
    day: string;
    time: string;
  }[];
  startDate: string;
  /**
   * Timezone identifier (e.g., "Asia/Kolkata", "America/New_York")
   * Used to display timezone abbreviation next to times
   */
  timezone?: string;
  /**
   * Array of tag details to display on the card
   */
  tagDetails?: Array<{
    color?: string;
    name: string;
    text: string;
    voucherLabel?: string;
  }>;
  /**
   * @default false
   */
  isSelected?: boolean;
  /**
   * Shows a "Tentative" badge next to the start date
   * @default false
   */
  tentative?: boolean;
  /**
   * Force a specific layout (overrides responsive behavior)
   * @default undefined (uses responsive behavior)
   */
  forceLayout?: 'desktop' | 'mobile';
  className?: string;
  onClick?: () => void;
  isRecommended?: boolean;
}

const SessionCard = React.forwardRef<HTMLDivElement, SessionCardProps>(
  (
    {
      scheduleDetails,
      startDate,
      timezone,
      tagDetails = [],
      isSelected = false,
      tentative = false,
      forceLayout,
      className,
      onClick,
      isRecommended: _isRecommended = false,
      ...props
    },
    ref
  ) => {
    const timezoneAbbr = timezone
      ? getTimezoneAbbreviation(timezone)
      : undefined;
    const isOngoing = tagDetails.some(tag => tag.name === 'Ongoing');
    const startLabel = isOngoing ? 'Batch started on' : 'Starts from';
    return (
      <div
        ref={ref}
        data-testid="session-card"
        data-selected={isSelected ? 'true' : 'false'}
        className={cn(
          'relative border border-dashed transition-all duration-200 focus:outline-none sm:rounded-3xl rounded-xl w-full',
          // Force specific layout or use responsive
          // (() => {
          //   if (forceLayout === 'desktop') {
          //     return 'w-60 h-48 rounded-3xl';
          //   }
          //   if (forceLayout === 'mobile') {
          //     return 'w-full h-auto rounded-xl';
          //   }
          //   // Default responsive behavior
          //   return 'w-60 h-48 rounded-3xl sm:w-60 sm:h-48 sm:rounded-3xl max-sm:w-full max-sm:h-auto max-sm:rounded-xl';
          // })(),
          isSelected
            ? 'bg-blue-chaos-light-cardbg border-blue-chaos-dark hover:shadow-lg hover:scale-105'
            : 'bg-magical-moonlight-white border-magical-moonlight-d1 hover:shadow-lg hover:border-magical-moonlight-d2 hover:scale-105',
          className
        )}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          onClick?.();
        }}
        tabIndex={0}
        role="button"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        {...props}
      >
        <div className="absolute -top-3.5 right-3 flex gap-2 items-center">
          {tagDetails.map((tag, index) => (
            <div
              key={index}
              className="text-magical-moonlight-white text-xs font-medium px-2 py-0.5 rounded"
              style={{
                backgroundColor:
                  tag.color ?? 'var(--color-magical-moonlight-d3)',
              }}
            >
              {tag.name === 'Ongoing' && tag.voucherLabel ? (
                <span>
                  💸 Get <span className="font-bold">{tag.voucherLabel}</span>{' '}
                  voucher
                </span>
              ) : (
                tag.text
              )}
            </div>
          ))}
        </div>

        <div
          className={cn(
            (() => {
              if (forceLayout === 'desktop') {
                return 'block p-4';
              }
              if (forceLayout === 'mobile') {
                return 'hidden';
              }
              return 'hidden sm:block p-4';
            })()
          )}
        >
          <div className="flex items-center gap-2 mb-4">
            <label className="flex items-center cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isSelected}
                  className="sr-only"
                  onChange={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    onClick?.();
                  }}
                />
                <div
                  className={cn(
                    'w-5 h-5 rounded border flex items-center justify-center transition-colors p-0.5',
                    isSelected
                      ? 'bg-underwater-fern-green border-underwater-fern-green'
                      : 'border-magical-moonlight-m bg-magical-moonlight-white'
                  )}
                >
                  {isSelected ? <CheckIcon color="white" /> : null}
                </div>
              </div>
              <span className="text-sm font-medium text-magical-moonlight-d1">
                &nbsp; Sessions will be on every
              </span>
            </label>
          </div>

          <div className="flex gap-6 mb-4 ml-6">
            {scheduleDetails.map((schedule, index) => (
              <div key={index} className="text-left">
                <div className="text-sm font-bold text-magical-moonlight-d3 mb-1">
                  {schedule.day}
                </div>
                <div className="text-sm font-bold text-blue-chaos-dark">
                  {schedule.time}
                  {timezoneAbbr ? (
                    <span className="text-sm font-bold text-blue-chaos-dark ml-1">
                      {timezoneAbbr}
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <div className="w-full border-t border-dashed border-magical-moonlight-d1 mb-4" />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 text-hot-sun">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-magical-moonlight-d1">
                {startLabel}
              </span>
            </div>
            {tentative ? (
              <span className="text-xs font-medium text-blue-chaos-dark bg-blue-chaos-light-cardbg px-2 py-0.5 rounded">
                Tentative
              </span>
            ) : null}
          </div>
          <div className="mt-1 ml-6">
            <span className="text-sm font-bold text-magical-moonlight-d3">
              {startDate}
            </span>
          </div>
        </div>

        <div
          className={cn(
            (() => {
              if (forceLayout === 'mobile') {
                return 'block p-2';
              }
              if (forceLayout === 'desktop') {
                return 'hidden';
              }
              return 'block sm:hidden p-2';
            })()
          )}
        >
          <div className="flex items-center justify-center gap-3">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-0.5">
                <div className="w-4 h-4 text-hot-sun">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-magical-moonlight-d1 leading-5">
                  Sessions will be on every
                </span>
              </div>
              <div className="flex items-center gap-3">
                {scheduleDetails.map((schedule, index) => (
                  <div key={index} className="flex items-center gap-0.5">
                    <span className="text-xs font-bold text-magical-moonlight-d3 leading-5">
                      {schedule.day}
                    </span>
                    <span className="text-xs font-bold text-blue-chaos-dark leading-5">
                      {schedule.time}
                      {timezoneAbbr ? (
                        <span className="text-xs font-bold text-blue-chaos-dark leading-5 ml-0.5">
                          {timezoneAbbr}
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-8 w-0 border-l border-magical-moonlight-d1" />

            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 text-hot-sun">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-magical-moonlight-d1 leading-5">
                  {startLabel}
                </span>
                {tentative ? (
                  <span className="text-xs font-medium text-blue-chaos-dark bg-blue-chaos-light-cardbg px-1.5 py-0.5 rounded">
                    Tentative
                  </span>
                ) : null}
              </div>
              <span className="text-xs font-bold text-magical-moonlight-d3 leading-5">
                {startDate}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

SessionCard.displayName = 'SessionCard';

export { SessionCard };
export default SessionCard;
